import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { AuthModalProvider } from "@/contexts/AuthModalContext";
import { Toaster } from "@/components/ui/sonner";
import { OrganizationSchema } from "@/components/SEO";
import ErrorBoundary from "@/components/ErrorBoundary";
import { HomePageSkeleton, CategoryPageSkeleton, ProductDetailSkeleton } from "@/components/Skeletons";
import RouteTracker from "@/components/RouteTracker";
import PageEngagement from "@/components/PageEngagement";
import VisitorHeartbeat from "@/components/VisitorHeartbeat";
import "./App.css";

// Layouts - keep sync as they're needed immediately
import StorefrontLayout from "@/modules/storefront/layouts/StorefrontLayout";
const AdminLayout = lazy(() => import("@/modules/admin/layouts/AdminLayout"));
const RiderLayout = lazy(() => import("@/modules/rider/layouts/RiderLayout"));
const POSLayout = lazy(() => import("@/modules/pos/layouts/POSLayout"));

// ══════════════════════════════════════════════════════════════════════════════
// STOREFRONT PAGES - Lazy loaded
// ══════════════════════════════════════════════════════════════════════════════
const Home = lazy(() => import("@/modules/storefront/pages/Home"));
const CategoryPage = lazy(() => import("@/modules/storefront/pages/CategoryPage"));
const ProductPage = lazy(() => import("@/modules/storefront/pages/ProductPage"));
const ReviewsPage = lazy(() => import("@/modules/storefront/pages/ReviewsPage"));
const CartPage = lazy(() => import("@/modules/storefront/pages/CartPage"));
const SearchPage = lazy(() => import("@/modules/storefront/pages/SearchPage"));
const CheckoutPage = lazy(() => import("@/modules/storefront/pages/CheckoutPage"));
const CheckoutCallbackPage = lazy(() => import("@/modules/storefront/pages/CheckoutCallbackPage"));
const OrderTracking = lazy(() => import("@/modules/storefront/pages/OrderTracking"));
const OrderSummaryPage = lazy(() => import("@/modules/storefront/pages/OrderSummaryPage"));
const OrderSuccess = lazy(() => import("@/modules/storefront/pages/OrderSuccess"));
const AccountPage = lazy(() => import("@/modules/storefront/pages/AccountPage"));
const SleepQuiz = lazy(() => import("@/modules/storefront/pages/SleepQuiz"));
const WishlistPage = lazy(() => import("@/modules/storefront/pages/WishlistPage"));
const AboutPage = lazy(() => import("@/modules/storefront/pages/AboutPage"));
const ContactPage = lazy(() => import("@/modules/storefront/pages/ContactPage"));
const ReturnRefundPolicy = lazy(() => import("@/modules/storefront/pages/ReturnRefundPolicy"));
const FAQPage = lazy(() => import("@/modules/storefront/pages/FAQPage"));
const CategoriesPage = lazy(() => import("@/modules/storefront/pages/CategoriesPage"));
const BrowsePage = lazy(() => import("@/modules/storefront/pages/BrowsePage"));
const GoogleAuthCallback = lazy(() => import("@/modules/storefront/pages/GoogleAuthCallback"));
const ReturnRequest = lazy(() => import("@/modules/storefront/pages/ReturnRequest"));
const CustomerOrderTracking = lazy(() => import("@/modules/storefront/pages/CustomerOrderTracking"));
const BlogPage = lazy(() => import("@/modules/storefront/pages/BlogPage"));
const BlogDetailPage = lazy(() => import("@/modules/storefront/pages/BlogDetailPage"));

// Preload frequently visited pages after initial render
const preloadPages = () => {
  const pages = [
    () => import("@/modules/storefront/pages/BlogPage"),
    () => import("@/modules/storefront/pages/FAQPage"),
    () => import("@/modules/storefront/pages/AboutPage"),
    () => import("@/modules/storefront/pages/ContactPage"),
    () => import("@/modules/storefront/pages/BrowsePage"),
    () => import("@/modules/storefront/pages/CategoryPage"),
  ];
  pages.forEach((load, i) => setTimeout(load, 1500 + i * 300));
};
if (typeof window !== "undefined") {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(preloadPages);
  } else {
    setTimeout(preloadPages, 2000);
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// ADMIN PAGES - Lazy loaded (heavy bundle)
// ══════════════════════════════════════════════════════════════════════════════
const AdminLogin = lazy(() => import("@/modules/admin/pages/AdminLogin"));
const AdminGoogleCallback = lazy(() => import("@/modules/admin/pages/AdminGoogleCallback"));
const AdminProfile = lazy(() => import("@/modules/admin/pages/AdminProfile"));
const Dashboard = lazy(() => import("@/modules/admin/pages/Dashboard"));
const CatalogManagement = lazy(() => import("@/modules/admin/pages/CatalogManagement"));
const MediaLibrary = lazy(() => import("@/modules/admin/pages/MediaLibrary"));
const ProductTypeManager = lazy(() => import("@/modules/admin/pages/ProductTypeManager"));
const GSTSettings = lazy(() => import("@/modules/admin/pages/GSTSettings"));
const CODSettings = lazy(() => import("@/modules/admin/pages/CODSettings"));
const OrderManagement = lazy(() => import("@/modules/admin/pages/OrderManagement"));
const DispatchConsole = lazy(() => import("@/modules/admin/pages/DispatchConsole"));
const ReturnsManagement = lazy(() => import("@/modules/admin/pages/ReturnsManagement"));
const WarrantyService = lazy(() => import("@/modules/admin/pages/WarrantyService"));
const CMSHub = lazy(() => import("@/modules/admin/pages/cms"));
const CouponsPage = lazy(() => import("@/modules/admin/pages/CouponsPage"));
const WalletPage = lazy(() => import("@/modules/admin/pages/WalletPage"));
const InventoryPage = lazy(() => import("@/modules/admin/pages/InventoryPage"));
const UserManagement = lazy(() => import("@/modules/admin/pages/UserManagement"));
const AuditLog = lazy(() => import("@/modules/admin/pages/AuditLog"));
const SupportConsole = lazy(() => import("@/modules/admin/pages/SupportConsole"));
const BulkOrders = lazy(() => import("@/modules/admin/pages/BulkOrders"));
const SalesAnalytics = lazy(() => import("@/modules/admin/pages/SalesAnalytics"));
const VariantsQuickPage = lazy(() => import("@/modules/admin/pages/VariantsQuickPage"));
const BlogManagement = lazy(() => import("@/modules/admin/pages/BlogManagement"));
const PopupSettings = lazy(() => import("@/modules/admin/pages/PopupSettings"));
const LeadsDashboard = lazy(() => import("@/modules/admin/pages/leads/LeadsDashboard"));
const LeadDetail = lazy(() => import("@/modules/admin/pages/leads/LeadDetail"));
const PushDashboard = lazy(() => import("@/modules/admin/pages/PushDashboard"));
const CRMDashboard = lazy(() => import("@/modules/admin/pages/CRMDashboard"));
const TaxonomyManager = lazy(() => import("@/modules/admin/pages/TaxonomyManager"));
const Customer360Page = lazy(() => import("@/modules/admin/pages/Customer360Page"));
const HomepageCMSEditor = lazy(() => import("@/modules/admin/pages/HomepageCMSEditor"));
const WorkflowManager = lazy(() => import("@/modules/admin/pages/WorkflowManager"));
const GSTDashboard = lazy(() => import("@/modules/admin/pages/GSTDashboard"));
const ReconciliationDashboard = lazy(() => import("@/modules/admin/pages/ReconciliationDashboard"));
const FinanceEmailSettings = lazy(() => import("@/modules/admin/pages/FinanceEmailSettings"));
const HoldDashboardPage = lazy(() => import("@/modules/admin/pages/HoldDashboardPage"));
const SalesEventsPage = lazy(() => import("@/modules/admin/pages/SalesEventsPage"));
const OrderDetail = lazy(() => import("@/modules/admin/pages/OrderDetail"));
const ReturnDetail = lazy(() => import("@/modules/admin/pages/ReturnDetail"));
const PaymentHealthDashboard = lazy(() => import("@/modules/admin/pages/PaymentHealthDashboard"));
const RefundDashboard = lazy(() => import("@/modules/admin/pages/RefundDashboard"));
const FailedShipments = lazy(() => import("@/modules/admin/pages/FailedShipments"));
const ExchangeOrders = lazy(() => import("@/modules/admin/pages/ExchangeOrders"));
const ExchangeOrderSummary = lazy(() => import("@/modules/admin/pages/ExchangeOrderSummary"));
const ProductAnalytics = lazy(() => import("@/modules/admin/pages/ProductAnalytics"));
const CategoryHealth = lazy(() => import("@/modules/admin/pages/CategoryHealth"));
const InventoryMovements = lazy(() => import("@/modules/admin/pages/InventoryMovements"));
const LowStockAlerts = lazy(() => import("@/modules/admin/pages/LowStockAlerts"));
const SKUInventory = lazy(() => import("@/modules/admin/pages/SKUInventory"));
const BulkProductEdit = lazy(() => import("@/modules/admin/pages/BulkProductEdit"));
const DeliveryPerformance = lazy(() => import("@/modules/admin/pages/DeliveryPerformance"));
const NDRManagement = lazy(() => import("@/modules/admin/pages/NDRManagement"));
const ShipmentExceptions = lazy(() => import("@/modules/admin/pages/ShipmentExceptions"));
const SLAEscalation = lazy(() => import("@/modules/admin/pages/SLAEscalation"));
const CancellationRequests = lazy(() => import("@/modules/admin/pages/CancellationRequests"));
const ReviewsModeration = lazy(() => import("@/modules/admin/pages/ReviewsModeration"));
const SiteAnalytics = lazy(() => import("@/modules/admin/pages/SiteAnalytics"));
const VisitorAnalytics = lazy(() => import("@/modules/admin/pages/VisitorAnalytics"));
const ClientErrors = lazy(() => import("@/modules/admin/pages/ClientErrors"));
const AttributeValuesPage = lazy(() => import("@/modules/admin/pages/AttributeValuesPage"));
const AttributeDefinitionsPage = lazy(() => import("@/modules/admin/pages/AttributeDefinitionsPage"));
const AttributeTemplatesPage = lazy(() => import("@/modules/admin/pages/AttributeTemplatesPage"));
const DataQualityPage = lazy(() => import("@/modules/admin/pages/DataQualityPage"));
const ProductOffersPage = lazy(() => import("@/modules/admin/pages/ProductOffersPage"));

// ══════════════════════════════════════════════════════════════════════════════
// RIDER PAGES - Lazy loaded
// ══════════════════════════════════════════════════════════════════════════════
const RiderLogin = lazy(() => import("@/modules/rider/pages/RiderLogin"));
const RiderDashboard = lazy(() => import("@/modules/rider/pages/RiderDashboard"));
const StopDetail = lazy(() => import("@/modules/rider/pages/StopDetail"));

// ══════════════════════════════════════════════════════════════════════════════
// POS PAGES - Lazy loaded
// ══════════════════════════════════════════════════════════════════════════════
const POSLogin = lazy(() => import("@/modules/pos/pages/POSLogin"));
const POSHome = lazy(() => import("@/modules/pos/pages/POSHome"));
const POSSale = lazy(() => import("@/modules/pos/pages/POSSale"));
const POSOrders = lazy(() => import("@/modules/pos/pages/POSOrders"));
const POSQuotations = lazy(() => import("@/modules/pos/pages/POSQuotations"));
const POSEmployees = lazy(() => import("@/modules/pos/pages/POSEmployees"));
const POSCustomer360 = lazy(() => import("@/modules/pos/pages/POSCustomer360"));
const POSDashboard = lazy(() => import("@/modules/pos/pages/POSDashboard"));
const POSCustomers = lazy(() => import("@/modules/pos/pages/POSCustomers"));
const POSInventory = lazy(() => import("@/modules/pos/pages/POSInventory"));
const POSSalesReport = lazy(() => import("@/modules/pos/pages/POSSalesReport"));
const POSStaffReport = lazy(() => import("@/modules/pos/pages/POSStaffReport"));
const POSProducts = lazy(() => import("@/modules/pos/pages/POSProducts"));
const POSCashManagement = lazy(() => import("@/modules/pos/pages/POSCashManagement"));

// ══════════════════════════════════════════════════════════════════════════════
// LOADING FALLBACKS
// ══════════════════════════════════════════════════════════════════════════════
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto mb-4" />
      <p className="text-sm text-gray-500">Loading...</p>
    </div>
  </div>
);

const AdminLoader = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="bg-white p-8 rounded-lg shadow-sm text-center">
      <div className="w-10 h-10 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
      <p className="text-gray-600">Loading Admin Panel...</p>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// ROUTE GUARDS
// ══════════════════════════════════════════════════════════════════════════════
function AdminGuard({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!isAuthenticated || !isAdmin) return <Navigate to="/admin/login" replace />;
  return children;
}

function RiderGuard({ children }) {
  const { isAuthenticated, isRider, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!isAuthenticated || !isRider) return <Navigate to="/rider/login" replace />;
  return children;
}

// ══════════════════════════════════════════════════════════════════════════════
// APP ROUTES
// ══════════════════════════════════════════════════════════════════════════════
function AppRoutes() {
  return (
    <>
    <RouteTracker />
    <PageEngagement />
    <VisitorHeartbeat />
    <Routes>
      {/* Google Auth callback (outside layout) */}
      <Route path="auth/google/callback" element={
        <Suspense fallback={<PageLoader />}>
          <GoogleAuthCallback />
        </Suspense>
      } />

      {/* Storefront */}
      <Route element={<StorefrontLayout />}>
        <Route index element={
          <Suspense fallback={<HomePageSkeleton />}>
            <Home />
          </Suspense>
        } />
        <Route path="category/:slug" element={
          <Suspense fallback={<CategoryPageSkeleton />}>
            <CategoryPage />
          </Suspense>
        } />
        <Route path="product/:slug" element={
          <Suspense fallback={<ProductDetailSkeleton />}>
            <ProductPage />
          </Suspense>
        } />
        <Route path="product/:slug/reviews" element={
          <Suspense fallback={<PageLoader />}>
            <ReviewsPage />
          </Suspense>
        } />
        <Route path="cart" element={
          <Suspense fallback={<PageLoader />}>
            <CartPage />
          </Suspense>
        } />
        <Route path="search" element={
          <Suspense fallback={<PageLoader />}>
            <SearchPage />
          </Suspense>
        } />
        <Route path="checkout" element={
          <Suspense fallback={<PageLoader />}>
            <CheckoutPage />
          </Suspense>
        } />
        <Route path="checkout/callback" element={
          <Suspense fallback={<PageLoader />}>
            <CheckoutCallbackPage />
          </Suspense>
        } />
        <Route path="order-summary/:orderId" element={
          <Suspense fallback={<PageLoader />}>
            <OrderSummaryPage />
          </Suspense>
        } />
        <Route path="exchange-order/:exchangeId" element={
          <Suspense fallback={<PageLoader />}>
            <ExchangeOrderSummary isAdmin={false} />
          </Suspense>
        } />
        <Route path="order-success/:orderNo" element={
          <Suspense fallback={<PageLoader />}>
            <OrderSuccess />
          </Suspense>
        } />
        <Route path="track" element={
          <Suspense fallback={<PageLoader />}>
            <OrderTracking />
          </Suspense>
        } />
        <Route path="track/:orderId" element={
          <Suspense fallback={<PageLoader />}>
            <OrderTracking />
          </Suspense>
        } />
        <Route path="order-tracking/:orderNo" element={
          <Suspense fallback={<PageLoader />}>
            <CustomerOrderTracking />
          </Suspense>
        } />
        <Route path="account" element={
          <Suspense fallback={<PageLoader />}>
            <AccountPage />
          </Suspense>
        } />
        <Route path="account/*" element={
          <Suspense fallback={<PageLoader />}>
            <AccountPage />
          </Suspense>
        } />
        <Route path="sleep-quiz" element={
          <Suspense fallback={<PageLoader />}>
            <SleepQuiz />
          </Suspense>
        } />
        <Route path="wishlist" element={
          <Suspense fallback={<PageLoader />}>
            <WishlistPage />
          </Suspense>
        } />
        <Route path="categories" element={
          <Suspense fallback={<PageLoader />}>
            <CategoriesPage />
          </Suspense>
        } />
        <Route path="browse" element={
          <Suspense fallback={<PageLoader />}>
            <BrowsePage />
          </Suspense>
        } />
        <Route path="about" element={
          <Suspense fallback={<PageLoader />}>
            <AboutPage />
          </Suspense>
        } />
        <Route path="contact" element={
          <Suspense fallback={<PageLoader />}>
            <ContactPage />
          </Suspense>
        } />
        <Route path="return-refund-policy" element={
          <Suspense fallback={<PageLoader />}>
            <ReturnRefundPolicy />
          </Suspense>
        } />
        <Route path="faq" element={
          <Suspense fallback={<PageLoader />}>
            <FAQPage />
          </Suspense>
        } />
        <Route path="return-request/:orderId" element={
          <Suspense fallback={<PageLoader />}>
            <ReturnRequest />
          </Suspense>
        } />
        <Route path="blog" element={
          <Suspense fallback={<PageLoader />}>
            <BlogPage />
          </Suspense>
        } />
        <Route path="blog/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <BlogDetailPage />
          </Suspense>
        } />
      </Route>

      {/* Admin */}
      <Route path="admin/login" element={
        <Suspense fallback={<AdminLoader />}>
          <AdminLogin />
        </Suspense>
      } />
      <Route path="admin/auth/google/callback" element={
        <Suspense fallback={<AdminLoader />}>
          <AdminGoogleCallback />
        </Suspense>
      } />
      <Route path="admin" element={
        <AdminGuard>
          <Suspense fallback={<AdminLoader />}>
            <AdminLayout />
          </Suspense>
        </AdminGuard>
      }>
        <Route index element={<Suspense fallback={<AdminLoader />}><Dashboard /></Suspense>} />
        <Route path="profile" element={<Suspense fallback={<AdminLoader />}><AdminProfile /></Suspense>} />
        <Route path="catalog" element={<Suspense fallback={<AdminLoader />}><CatalogManagement /></Suspense>} />
        <Route path="media" element={<Suspense fallback={<AdminLoader />}><MediaLibrary /></Suspense>} />
        <Route path="product-types" element={<Suspense fallback={<AdminLoader />}><ProductTypeManager /></Suspense>} />
        <Route path="settings/gst" element={<Suspense fallback={<AdminLoader />}><GSTSettings /></Suspense>} />
        <Route path="settings/cod" element={<Suspense fallback={<AdminLoader />}><CODSettings /></Suspense>} />
        <Route path="orders" element={<Suspense fallback={<AdminLoader />}><OrderManagement /></Suspense>} />
        <Route path="orders/:orderId" element={<Suspense fallback={<AdminLoader />}><OrderDetail /></Suspense>} />
        <Route path="inventory" element={<Suspense fallback={<AdminLoader />}><InventoryPage /></Suspense>} />
        <Route path="variants" element={<Suspense fallback={<AdminLoader />}><VariantsQuickPage /></Suspense>} />
        <Route path="dispatch" element={<Suspense fallback={<AdminLoader />}><DispatchConsole /></Suspense>} />
        <Route path="returns" element={<Suspense fallback={<AdminLoader />}><ReturnsManagement /></Suspense>} />
        <Route path="returns/:returnId" element={<Suspense fallback={<AdminLoader />}><ReturnDetail /></Suspense>} />
        <Route path="warranty" element={<Suspense fallback={<AdminLoader />}><WarrantyService /></Suspense>} />
        <Route path="coupons" element={<Suspense fallback={<AdminLoader />}><CouponsPage /></Suspense>} />
        <Route path="wallet" element={<Suspense fallback={<AdminLoader />}><WalletPage /></Suspense>} />
        <Route path="sales-events" element={<Suspense fallback={<AdminLoader />}><SalesEventsPage /></Suspense>} />
        <Route path="cms-hub" element={<Suspense fallback={<AdminLoader />}><CMSHub /></Suspense>} />
        <Route path="cms" element={<Suspense fallback={<AdminLoader />}><CMSHub /></Suspense>} />
        <Route path="users" element={<Suspense fallback={<AdminLoader />}><UserManagement /></Suspense>} />
        <Route path="audit" element={<Suspense fallback={<AdminLoader />}><AuditLog /></Suspense>} />
        <Route path="support" element={<Suspense fallback={<AdminLoader />}><SupportConsole /></Suspense>} />
        <Route path="bulk-orders" element={<Suspense fallback={<AdminLoader />}><BulkOrders /></Suspense>} />
        <Route path="analytics" element={<Suspense fallback={<AdminLoader />}><SalesAnalytics /></Suspense>} />
        <Route path="leads" element={<Suspense fallback={<AdminLoader />}><LeadsDashboard /></Suspense>} />
        <Route path="leads/:leadId" element={<Suspense fallback={<AdminLoader />}><LeadDetail /></Suspense>} />
        <Route path="push" element={<Suspense fallback={<AdminLoader />}><PushDashboard /></Suspense>} />
        <Route path="crm" element={<Suspense fallback={<AdminLoader />}><CRMDashboard /></Suspense>} />
        <Route path="taxonomy" element={<Suspense fallback={<AdminLoader />}><TaxonomyManager /></Suspense>} />
        <Route path="customer360" element={<Suspense fallback={<AdminLoader />}><Customer360Page /></Suspense>} />
        <Route path="hold-dashboard" element={<Suspense fallback={<AdminLoader />}><HoldDashboardPage /></Suspense>} />
        <Route path="cms/homepage" element={<Suspense fallback={<AdminLoader />}><HomepageCMSEditor /></Suspense>} />
        <Route path="workflows" element={<Suspense fallback={<AdminLoader />}><WorkflowManager /></Suspense>} />
        <Route path="blog" element={<Suspense fallback={<AdminLoader />}><BlogManagement /></Suspense>} />
        <Route path="popup-settings" element={<Suspense fallback={<AdminLoader />}><PopupSettings /></Suspense>} />
        <Route path="gst-dashboard" element={<Suspense fallback={<AdminLoader />}><GSTDashboard /></Suspense>} />
        <Route path="reconciliation" element={<Suspense fallback={<AdminLoader />}><ReconciliationDashboard /></Suspense>} />
        <Route path="finance-email" element={<Suspense fallback={<AdminLoader />}><FinanceEmailSettings /></Suspense>} />
        <Route path="payment-health" element={<Suspense fallback={<AdminLoader />}><PaymentHealthDashboard /></Suspense>} />
        <Route path="refunds" element={<Suspense fallback={<AdminLoader />}><RefundDashboard /></Suspense>} />
        <Route path="failed-shipments" element={<Suspense fallback={<AdminLoader />}><FailedShipments /></Suspense>} />
        <Route path="exchange-orders" element={<Suspense fallback={<AdminLoader />}><ExchangeOrders /></Suspense>} />
        <Route path="exchange-orders/:exchangeId" element={<Suspense fallback={<AdminLoader />}><ExchangeOrderSummary isAdmin={true} /></Suspense>} />
        <Route path="product-analytics" element={<Suspense fallback={<AdminLoader />}><ProductAnalytics /></Suspense>} />
        <Route path="category-health" element={<Suspense fallback={<AdminLoader />}><CategoryHealth /></Suspense>} />
        <Route path="inventory-movements" element={<Suspense fallback={<AdminLoader />}><InventoryMovements /></Suspense>} />
        <Route path="low-stock-alerts" element={<Suspense fallback={<AdminLoader />}><LowStockAlerts /></Suspense>} />
        <Route path="sku-inventory" element={<Suspense fallback={<AdminLoader />}><SKUInventory /></Suspense>} />
        <Route path="bulk-product-edit" element={<Suspense fallback={<AdminLoader />}><BulkProductEdit /></Suspense>} />
        <Route path="delivery-performance" element={<Suspense fallback={<AdminLoader />}><DeliveryPerformance /></Suspense>} />
        <Route path="ndr-management" element={<Suspense fallback={<AdminLoader />}><NDRManagement /></Suspense>} />
        <Route path="shipment-exceptions" element={<Suspense fallback={<AdminLoader />}><ShipmentExceptions /></Suspense>} />
        <Route path="sla-escalation" element={<Suspense fallback={<AdminLoader />}><SLAEscalation /></Suspense>} />
        <Route path="cancellation-requests" element={<Suspense fallback={<AdminLoader />}><CancellationRequests /></Suspense>} />
        <Route path="reviews" element={<Suspense fallback={<AdminLoader />}><ReviewsModeration /></Suspense>} />
        <Route path="site-analytics" element={<Suspense fallback={<AdminLoader />}><SiteAnalytics /></Suspense>} />
        <Route path="visitor-analytics" element={<Suspense fallback={<AdminLoader />}><VisitorAnalytics /></Suspense>} />
        <Route path="client-errors" element={<Suspense fallback={<AdminLoader />}><ClientErrors /></Suspense>} />
        <Route path="attribute-values" element={<Suspense fallback={<AdminLoader />}><AttributeValuesPage /></Suspense>} />
        <Route path="attribute-definitions" element={<Suspense fallback={<AdminLoader />}><AttributeDefinitionsPage /></Suspense>} />
        <Route path="attribute-templates" element={<Suspense fallback={<AdminLoader />}><AttributeTemplatesPage /></Suspense>} />
        <Route path="data-quality" element={<Suspense fallback={<AdminLoader />}><DataQualityPage /></Suspense>} />
        <Route path="product-offers" element={<Suspense fallback={<AdminLoader />}><ProductOffersPage /></Suspense>} />
      </Route>

      {/* Rider */}
      <Route path="rider/login" element={
        <Suspense fallback={<PageLoader />}>
          <RiderLogin />
        </Suspense>
      } />
      <Route path="rider" element={
        <RiderGuard>
          <Suspense fallback={<PageLoader />}>
            <RiderLayout />
          </Suspense>
        </RiderGuard>
      }>
        <Route index element={<Suspense fallback={<PageLoader />}><RiderDashboard /></Suspense>} />
        <Route path="stop/:stopId" element={<Suspense fallback={<PageLoader />}><StopDetail /></Suspense>} />
      </Route>

      {/* POS (Point of Sale) */}
      <Route path="pos/login" element={
        <Suspense fallback={<PageLoader />}>
          <POSLogin />
        </Suspense>
      } />
      <Route path="pos" element={
        <Suspense fallback={<PageLoader />}>
          <POSLayout />
        </Suspense>
      }>
        <Route index element={<Navigate to="/pos/products" replace />} />
        <Route path="products" element={<Suspense fallback={<PageLoader />}><POSProducts /></Suspense>} />
        <Route path="sale" element={<Navigate to="/pos/products" replace />} />
        <Route path="orders" element={<Suspense fallback={<PageLoader />}><POSOrders /></Suspense>} />
        <Route path="quotations" element={<Suspense fallback={<PageLoader />}><POSQuotations /></Suspense>} />
        <Route path="employees" element={<Suspense fallback={<PageLoader />}><POSEmployees /></Suspense>} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><POSDashboard /></Suspense>} />
        <Route path="customers" element={<Suspense fallback={<PageLoader />}><POSCustomers /></Suspense>} />
        <Route path="inventory" element={<Suspense fallback={<PageLoader />}><POSInventory /></Suspense>} />
        <Route path="cash" element={<Suspense fallback={<PageLoader />}><POSCashManagement /></Suspense>} />
        <Route path="reports/sales" element={<Suspense fallback={<PageLoader />}><POSSalesReport /></Suspense>} />
        <Route path="reports/staff" element={<Suspense fallback={<PageLoader />}><POSStaffReport /></Suspense>} />
        <Route path="customer360" element={<Suspense fallback={<PageLoader />}><POSCustomer360 /></Suspense>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════════════════════
function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
            <AuthModalProvider>
            <CartProvider>
              <OrganizationSchema />
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </CartProvider>
            </AuthModalProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
